import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ShieldCheck, Star, ArrowUpRight, Heart } from "lucide-react";
import { Logo } from "./Logo";
import { SITE, NAV, CRISIS_RESOURCES } from "../data/content";

const LEGAL = [
  { label: "FAQ", to: "/faq" },
  { label: "Privacy", to: "/privacy" },
  { label: "Terms", to: "/terms" },
  { label: "Accessibility", to: "/accessibility" },
];

export const Footer = () => (
  <footer className="bg-navy text-offwhite" data-testid="site-footer">
    <div className="h-1 w-full bg-gold" aria-hidden="true" />
    <div className="container-x py-16 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Brand + trust + crisis */}
        <div className="lg:col-span-5">
          <Logo light />
          <p className="mt-6 max-w-sm text-offwhite/70 leading-relaxed">{SITE.mission}</p>

          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="footer-trust-badges">
            <span className="inline-flex items-center gap-2 rounded-full border border-offwhite/20 px-3.5 py-1.5 text-xs font-semibold text-offwhite/85">
              <ShieldCheck className="h-3.5 w-3.5 text-gold" /> 501(c)(3) In Formation
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-offwhite/20 px-3.5 py-1.5 text-xs font-semibold text-offwhite/85">
              <Star className="h-3.5 w-3.5 text-gold" /> Veteran-Founded
            </span>
          </div>

          <div className="mt-6 rounded-xl border border-gold/30 bg-white/5 p-5" data-testid="footer-crisis">
            <div className="label-caps text-gold flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> In immediate crisis?</div>
            <p className="mt-2 text-sm text-offwhite/85 leading-relaxed">
              Dial <a href="tel:988" className="font-bold underline decoration-gold/50 underline-offset-2 hover:text-gold transition-colors">988, then Press 1</a> for the Veterans Crisis Line — 24/7, confidential, free.
            </p>
          </div>
        </div>

        {/* Quick navigation */}
        <div className="lg:col-span-3">
          <div className="label-caps text-gold mb-5 pb-3 border-b border-offwhite/15">Quick Navigation</div>
          <ul className="space-y-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-offwhite/75 hover:text-gold transition-colors" data-testid={`footer-nav-${n.label.split(" ")[0].toLowerCase()}`}>
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact & outreach */}
        <div className="lg:col-span-4">
          <div className="label-caps text-gold mb-5 pb-3 border-b border-offwhite/15">Contact & Outreach Center</div>
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3 text-offwhite/80">
              <MapPin className="h-4 w-4 text-gold shrink-0 mt-0.5" /> {SITE.campus}
            </li>
            <li>
              <a href={`tel:${SITE.hotline}`} className="flex items-center gap-3 text-offwhite/80 hover:text-gold transition-colors" data-testid="footer-hotline">
                <Phone className="h-4 w-4 text-gold shrink-0" /> Intake Hotline: <span className="font-semibold">{SITE.hotline}</span>
              </a>
            </li>
            <li>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 text-offwhite/80 hover:text-gold transition-colors" data-testid="footer-email">
                <Mail className="h-4 w-4 text-gold shrink-0" /> {SITE.email}
              </a>
            </li>
          </ul>
          <p className="mt-5 text-sm text-offwhite/55"><span className="font-semibold text-offwhite/80">Primary Service Area:</span> {SITE.serviceArea}.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="footer-inquiry-btn" className="group inline-flex items-center gap-2 rounded-lg border border-offwhite/25 px-5 py-3 text-sm font-semibold text-offwhite hover:bg-offwhite hover:text-navy transition-colors">
              Send Direct Inquiry <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/donate" data-testid="footer-donate-btn" className="group inline-flex items-center gap-2 rounded-lg bg-gold px-5 py-3 text-sm font-bold text-charcoal hover:bg-gold-deep transition-colors">
              <Heart className="h-4 w-4" /> Donate
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-offwhite/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <p className="text-xs md:text-sm text-offwhite/55 uppercase tracking-wide">© {new Date().getFullYear()} {SITE.name} • Nonprofit Organization</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {LEGAL.map((l) => (
            <Link key={l.label} to={l.to} className="text-sm text-offwhite/55 hover:text-gold transition-colors" data-testid={`footer-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
